import * as THREE from 'three';
import { bakeColored } from './batch';
import { makeSignTexture, mesh } from './helpers';
import type { ParkMaterials } from './materials';
import type { PathPiece } from './terrain';

export type QueueSlot = {
  x: number;
  z: number;
  facing: number;
  phase: number;
  hidden?: boolean;
};

export type QueueLayout = {
  start: THREE.Vector2;
  end: THREE.Vector2;
  width: number;
};

export function queueLaneFrame(layout: QueueLayout): {
  dir: THREE.Vector2;
  side: THREE.Vector2;
  length: number;
  yaw: number;
} {
  const dir = new THREE.Vector2().subVectors(layout.end, layout.start);
  const length = dir.length();
  dir.divideScalar(length);
  return { dir, side: new THREE.Vector2(-dir.y, dir.x), length, yaw: Math.atan2(dir.x, dir.y) };
}

/** Paved strip merged into the park path network. */
export function queueLanePiece(layout: QueueLayout): PathPiece {
  const { length, yaw } = queueLaneFrame(layout);
  const center = new THREE.Vector2().addVectors(layout.start, layout.end).multiplyScalar(0.5);
  return { kind: 'rect', x: center.x, z: center.y, w: layout.width, d: length + 0.4, radius: 0.3, rotY: yaw };
}

/** Single file from the ride steps backwards, with a little lateral shuffle so it does not read as a grid. */
export function createQueueSlots(layout: QueueLayout, count: number, spacing = 0.37): QueueSlot[] {
  const { dir, side } = queueLaneFrame(layout);
  const facing = Math.atan2(dir.x, dir.y);
  const slots: QueueSlot[] = [];
  for (let i = 0; i < count; i += 1) {
    const back = 0.18 + i * spacing;
    const shuffle = (i % 2 === 0 ? -1 : 1) * 0.055 + Math.sin(i * 2.3) * 0.03;
    const point = layout.end.clone().addScaledVector(dir, -back).addScaledVector(side, shuffle);
    slots.push({ x: point.x, z: point.y, facing: facing + Math.sin(i * 1.7) * 0.22, phase: i * 0.7 });
  }
  return slots;
}

/** Roped stanchions along both sides of the lane plus a small board at the lane entrance. */
export function createCarouselQueue(
  mats: ParkMaterials,
  layout: QueueLayout,
  parkTop: number,
  boardLabel = 'RIDE',
): THREE.Group {
  const root = new THREE.Group();
  const { dir, side, length } = queueLaneFrame(layout);
  const proto = new THREE.Group();
  const half = layout.width / 2 + 0.02;
  const postCount = Math.max(2, Math.round(length / 0.72) + 1);
  const railHeight = 0.5;
  for (const sign of [-1, 1]) {
    const posts: THREE.Vector3[] = [];
    for (let i = 0; i < postCount; i += 1) {
      const point = layout.start.clone().addScaledVector(dir, (length / (postCount - 1)) * i).addScaledVector(side, sign * half);
      posts.push(new THREE.Vector3(point.x, 0, point.y));
      proto.add(mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.05, 8), mats.lampPost, { position: [point.x, 0.025, point.y] }));
      proto.add(mesh(new THREE.CylinderGeometry(0.028, 0.032, railHeight, 6), mats.lampPost, { position: [point.x, railHeight / 2, point.y] }));
      proto.add(mesh(new THREE.SphereGeometry(0.05, 8, 6), mats.gold, { position: [point.x, railHeight + 0.04, point.y] }));
    }
    for (let i = 0; i < posts.length - 1; i += 1) {
      const a = posts[i]!.clone().setY(railHeight - 0.02);
      const b = posts[i + 1]!.clone().setY(railHeight - 0.02);
      const mid = a.clone().add(b).multiplyScalar(0.5).setY(railHeight - 0.13);
      const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
      proto.add(mesh(new THREE.TubeGeometry(curve, 6, 0.018, 5, false), mats.rope, { cast: false }));
    }
  }
  const entry = layout.start.clone().addScaledVector(side, half + 0.16).addScaledVector(dir, 0.1);
  proto.add(mesh(new THREE.CylinderGeometry(0.03, 0.035, 1.05, 6), mats.lampPost, { position: [entry.x, 0.52, entry.y] }));
  const queueMesh = new THREE.Mesh(bakeColored(proto), mats.baked);
  queueMesh.position.y = parkTop;
  queueMesh.castShadow = true;
  queueMesh.receiveShadow = false;
  root.add(queueMesh);

  const sign = mesh(
    new THREE.PlaneGeometry(0.62, 0.22),
    new THREE.MeshStandardMaterial({ map: makeSignTexture(boardLabel, '#fff6e6', '#e24a4a', '#f2c14e'), roughness: 0.6, side: THREE.DoubleSide }),
    { cast: false, receive: false },
  );
  sign.position.set(entry.x, parkTop + 0.95, entry.y);
  sign.rotation.y = Math.atan2(side.x, side.y) + Math.PI;
  root.add(sign);
  return root;
}

/** Mechanical alias: the approved Fable rope/lane factory is attraction-generic. */
export const createRideQueue = createCarouselQueue;
